
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { formatPrice } from '@/modules/Helpers';
import { format, parseISO } from 'date-fns';
import { VAT_EXCLUSION_FACTOR } from '../constants/index';

export default {
    props: {
        dish: { type: Object, required: true },
    },
    computed: {
        ...mapGetters('order', [
            'items',
            'restaurantLocation',
            'isDelivery',
            'itemsCount',
            'address',
            'groupCartUUID',
            'groupCartSlave',
        ]),
        ...mapGetters(['vat']),
        cartItems() {
            return this.items.filter(
                item =>
                    item.id === this.dish.id &&
                    (this.groupCartUUID && !this.groupCartSlave ? !item.added_by_name : true)
            );
        },
        quantity() {
            return this.dish.food_option_groups.length ? false : this.cartItems.reduce((sum, x) => sum + x.quantity, 0);
        },
        price() {
            return this.dish.price === 0
                ? 'GRATIS'
                : formatPrice(this.vat === 'excl' ? this.dish.price * VAT_EXCLUSION_FACTOR : this.dish.price);
        },
        foodOptionGroupsCount() {
            return this.dish.food_option_groups.length;
        },
        hasDishSheet() {
            return this.foodOptionGroupsCount > 0;
        },
        launchingSoon() {
            return this.restaurantLocation?.launching_soon;
        },
        restaurantName() {
            return this.restaurantLocation.restaurant_name;
        },
        accessedViaRestaurantChain() {
            if (process.browser) {
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const backRedirectTo = urlParams.get('backRedirectTo');
                if (backRedirectTo && backRedirectTo.includes('supplier-chain')) {
                    return true;
                }
            }
            return false;
        },
        deliverItClosed() {
            if (this.isDelivery) {
                if (this.restaurantLocation) {
                    if (this.restaurantLocation.delivery_option) {
                        if (this.restaurantLocation.delivery_option.type === 'deliverit') {
                            if (this.restaurantLocation.delivery_option.closed) {
                                return true;
                            }
                        }
                    }
                }
            }
            return false;
        },
    },
    methods: {
        ...mapMutations('sheet', ['setSheet']),
        ...mapMutations('sideDrawer', ['setSideDrawer']),
        ...mapMutations('modal', ['setModal']),
        ...mapActions('order', ['addDish', 'updateDish']),
        ...mapActions('toast', ['setToast']),
        add() {
            if (this.validateRestaurantAvailability()) {
                // If validation passes, add the dish
                this.addDish({
                    ...this.dish,
                    food_option_groups: [],
                    menu: false,
                    quantity: 1,
                });
            }
        },
        change(type) {
            const add = type === 'add';
            const quantity = add ? this.quantity + 1 : this.quantity - 1;

            // Trial item logic
            if (this.dish.trial_item) {
                if (add) {
                    this.setToast({
                        msg: 'Du kan max tilføje ét test-produkt',
                    });
                    return;
                }
            }

            if (this.hasDishSheet) {
                if (add) {
                    this.setModal({ type: 'Dish', data: { dish: this.dish } });
                } else if (this.cartItems.length > 1) {
                    this.setSideDrawer({ type: 'Cart' });
                } else {
                    this.updateDish({ uuid: this.cartItems[0].uuid, quantity });
                }
            } else {
                this.updateDish({ uuid: this.cartItems[0].uuid, quantity });
            }
        },
        validateRestaurantAvailability() {
            if (this.launchingSoon && !this.accessedViaRestaurantChain) {
                this.setToast({ msg: `${this.restaurantName} launcher snart. Du kan inden længe bestille herfra 👋` });
                return false;
            }
            if (this.restaurantLocation.temp_closed) {
                this.setToast({
                    msg: 'Restauranten har desværre midlertidigt lukket, men du kan stadig browse menukortet. Vi beklager ulejligheden.',
                });
                return false;
            }

            if (!this.restaurantLocation.preorder_enabled) {
                const openHoursNextStr = this.restaurantLocation.open_hours_next
                    ? `Restauranten åbner igen kl. ${format(parseISO(this.restaurantLocation.open_hours_next), 'HH:mm')}`
                    : '';
                if (!this.restaurantLocation.is_open) {
                    this.setToast({
                        msg: `Restauranten har lukket, men du kan stadig browse menukortet. ${openHoursNextStr}`,
                    });
                    return false;
                }
                if (!this.restaurantLocation.kitchen_is_open) {
                    this.setToast({
                        msg: `Restaurantens køkken tager ikke imod flere bestillinger i dag. ${openHoursNextStr}`,
                    });
                    return false;
                }
                if (this.deliverItClosed) {
                    this.setToast({ msg: `Levering fra ${this.restaurantName} kan ikke bestilles lige nu.` });
                    return false;
                }
            }

            if (this.dish.trial_item) {
                if (this.items.some(x => !x.trial_item)) {
                    this.setToast({
                        msg: 'Du kan kun tilføje et test-produkt hvis du ikke har andre produkter i kurven.',
                    });
                    return false;
                }
            } else if (this.items.some(x => x.trial_item)) {
                this.setToast({ msg: 'Du kan ikke tilføje andre produkter, når du har et test-produkt i kurven.' });
                return false;
            }

            if (this.hasDishSheet) {
                this.setModal({ type: 'Dish', data: { dish: this.dish } });
            } else if (this.itemsCount === 0) {
                setTimeout(() => this.setSideDrawer({ type: 'Cart' }), 100);
            }

            return true;
        },
    },
};
